import {
  createContext,
  useContext,
  useMemo,
  type FC,
  type ReactNode,
} from 'react'
import type { AssetVizDataConfig } from './AssetVizDataConfig'
import { defaultDataConfig } from './defaultDataConfig'

const AssetVizDataContext = createContext<AssetVizDataConfig>(defaultDataConfig)

/**
 * Provider that injects data-fetching hook implementations into the AssetViz
 * component tree. The host application wraps AssetViz with this provider and
 * supplies its own hook implementations for color scales, CDF data, URL
 * resolution, and WebGL context management.
 *
 * Only the hooks that differ from defaults need to be provided — the config
 * is merged with defaultDataConfig so consumers can override selectively.
 */
export const AssetVizDataProvider: FC<{
  config: Partial<AssetVizDataConfig>
  children: ReactNode
}> = ({ config, children }) => {
  const merged = useMemo<AssetVizDataConfig>(
    () => ({
      ...defaultDataConfig,
      ...config,
    }),
    [config]
  )

  return (
    <AssetVizDataContext.Provider value={merged}>
      {children}
    </AssetVizDataContext.Provider>
  )
}

/**
 * Hook to access data-fetching implementations from the nearest
 * AssetVizDataProvider. Falls back to safe defaults if no provider
 * is present in the tree.
 */
export function useAssetVizData(): AssetVizDataConfig {
  return useContext(AssetVizDataContext)
}
